import { tableTableSelectionReducer } from './state';
import type { TableTableOption, TableSelectionPersistedState, TableSelectionState } from './state';
import { computed, createModel, effect, signal, untracked } from '@preact/signals-react';
import type { ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import { compareKey, parseKey, stringifyKey } from '@thinkalpha/table-client';
import type { Key } from '@thinkalpha/table-client';
import { TableClass } from '@thinkalpha/ui-types/contracts/workspace';
import type { ProxyClientService } from 'src/services/ProxyClientService';
import type { TableWidgetEvents, TableWidgetSignals } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type { EventBus } from 'src/types/EventBus/EventBus';

export type { TableTableOption, TableSelectionPersistedState } from './state';

export interface TableTableClassOption {
    label: string;
    value: TableClass | null;
}

const TABLE_CLASS_OPTIONS: readonly TableTableClassOption[] = [
    { label: 'None', value: null },
    { label: 'Platforms', value: TableClass.platforms },
    { label: 'Alpha Accounts', value: TableClass.joinedAccounts },
    { label: 'Accounts', value: TableClass.accounts },
    { label: 'Symbols', value: TableClass.symbols },
    { label: 'Traders', value: TableClass.traders },
    { label: 'Positions', value: TableClass.positions },
    { label: 'Alpha Positions', value: TableClass.joinedPositions },
    { label: 'No Positions', value: TableClass.missingPositions },
    { label: 'Fills', value: TableClass.fills },
    { label: 'Orders', value: TableClass.orders },
];

export function indexTableKeys(keys: readonly string[]): readonly TableTableOption[] {
    return keys
        .values()
        .unique()
        .map((value) => ({
            label: value,
            normalizedLabel: value.toLowerCase(),
            value,
        }))
        .toArray();
}

export function filterTableKeys(options: readonly TableTableOption[], rawQuery: string): readonly TableTableOption[] {
    const query = rawQuery.trim().toLowerCase();
    if (query === '') return options;

    const prefixMatches: TableTableOption[] = [];
    const substringMatches: TableTableOption[] = [];
    for (const option of options) {
        const matchIndex = option.normalizedLabel.indexOf(query);
        if (matchIndex === 0) {
            prefixMatches.push(option);
        } else if (matchIndex > 0) {
            substringMatches.push(option);
        }
    }
    return prefixMatches.concat(substringMatches);
}

export interface TableSelectionModelShape {
    readonly classOptions: ReadonlySignal<readonly TableTableClassOption[]>;
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: EventBus<TableWidgetSignals, TableWidgetEvents>;
    readonly filteredTableKeyOptions: ReadonlySignal<readonly TableTableOption[]>;
    readonly tableKeyResultSummary: ReadonlySignal<string>;
    readonly searchQuery: ReadonlySignal<string>;
    readonly selectedTableKey: ReadonlySignal<string | null>;
    readonly tableSettingsOpen: ReadonlySignal<boolean>;
    readonly table: ReadonlySignal<Key | null>;
    readonly tableClass: ReadonlySignal<TableClass | null>;
    readonly tableKeyOptions: ReadonlySignal<readonly TableTableOption[]>;
    markClean(): void;
    selectTableKey(value: string): void;
    setTableSettingsOpen(open: boolean): void;
    setSearchQuery(query: string): void;
    setTableClass(tableClass: TableClass | null): void;
    toJSON(): TableSelectionPersistedState;
}

export interface TableSelectionModelInit {
    persisted: TableSelectionPersistedState;
    proxyClientService: ProxyClientService;
}

export const TableSelectionModel: ModelConstructor<TableSelectionModelShape, [TableSelectionModelInit]> = createModel<
    TableSelectionModelShape,
    [TableSelectionModelInit]
>((init: TableSelectionModelInit) => {
    const state = signal<TableSelectionState>({
        table: init.persisted.table,
        tableClass: init.persisted.class,
        tableKeyOptions: [],
        searchQuery: '',
        tableSettingsOpen: false,
    });
    const cleanPersisted = signal<TableSelectionPersistedState>(init.persisted);

    const dispatch = (action: Parameters<typeof tableTableSelectionReducer>[1]): void => {
        state.value = tableTableSelectionReducer(state.value, action);
    };

    let keysSubscription: { unsubscribe(): void } | undefined;
    effect(() => {
        const client = init.proxyClientService.target.value.client;
        untracked(() => {
            keysSubscription?.unsubscribe();
            dispatch({ type: 'SET_TABLE_KEY_OPTIONS', options: [] });
            keysSubscription = client.keys$.subscribe((keys) =>
                dispatch({ type: 'SET_TABLE_KEY_OPTIONS', options: indexTableKeys(keys) }),
            );
        });
        return () => keysSubscription?.unsubscribe();
    });

    const table = computed(() => state.value.table);
    const tableClass = computed(() => state.value.tableClass);
    const selectedTableKey = computed(() => stringifyKey(table.value));
    const tableKeyOptions = computed(() => state.value.tableKeyOptions);
    const filteredTableKeyOptions = computed(() => filterTableKeys(tableKeyOptions.value, state.value.searchQuery));
    const persisted = computed<TableSelectionPersistedState>(() => ({
        table: table.value,
        class: tableClass.value,
    }));
    const dirty = computed(
        () =>
            !(persisted.value.table === null
                ? cleanPersisted.value.table === null
                : compareKey(persisted.value.table, cleanPersisted.value.table ?? undefined)) ||
            persisted.value.class !== cleanPersisted.value.class,
    );
    const tableKeys = computed(() => tableKeyOptions.value.map((option) => option.value));
    const tableSettingsOpen = computed(() => state.value.tableSettingsOpen);
    const searchQuery = computed(() => state.value.searchQuery);
    const tableKeyResultSummary = computed(() => {
        const matches = filteredTableKeyOptions.value.length;
        const total = tableKeyOptions.value.length;
        return matches === total
            ? `${total.toLocaleString()} tables`
            : `${matches.toLocaleString()} of ${total.toLocaleString()} tables`;
    });
    const classOptions = signal(TABLE_CLASS_OPTIONS);

    const selectTableKey = (value: string): void => dispatch({ type: 'SELECT_TABLE', table: parseKey(value) });
    const updateTableClass = (value: TableClass | null): void =>
        dispatch({ type: 'SET_TABLE_CLASS', tableClass: value });
    const updateTableSettingsOpen = (open: boolean): void => dispatch({ type: 'SET_TABLE_SETTINGS_OPEN', open });

    const eventBus = EventBusBuilder.create()
        .addEvent('widget::onTableKeySelect', ({ value }) => selectTableKey(value))
        .addEvent('widget::onTableClassSelect', ({ tableClass: value }) => updateTableClass(value))
        .addEvent('widget::setShowTableSettingsDialog', ({ show }) => updateTableSettingsOpen(show))
        .addSignal('widget::selectedTableKey', () => selectedTableKey)
        .addSignal('widget::showTableSettingsDialog', () => tableSettingsOpen)
        .addSignal('widget::tableKeys', () => tableKeys)
        .build();

    return {
        classOptions,
        dirty,
        eventBus,
        filteredTableKeyOptions,
        searchQuery,
        selectedTableKey,
        tableKeyResultSummary,
        tableSettingsOpen,
        table,
        tableClass,
        tableKeyOptions,
        markClean(): void {
            cleanPersisted.value = persisted.value;
        },
        selectTableKey,
        setTableSettingsOpen: updateTableSettingsOpen,
        setSearchQuery(query: string): void {
            dispatch({ type: 'SET_SEARCH_QUERY', query });
        },
        setTableClass: updateTableClass,
        toJSON(): TableSelectionPersistedState {
            return persisted.value;
        },
    };
});

export type TableSelectionModel = InstanceType<typeof TableSelectionModel>;
